import { useLocalStorage } from '@mantine/hooks';
import {
  BackendFetch,
  DevTools,
  I18nextPlugin,
  Tolgee,
  withTolgee,
} from '@tolgee/i18next';
import { BASENAME, DEFAULT_LANGUAGE, STORAGE_KEYS } from 'consts';
import { setDefaultOptions } from 'date-fns';
import { enUS, pl } from 'date-fns/locale';
import { useGetUser } from 'entities/user/api';
import { env } from 'env';
import { getNavigatorLanguage } from 'helpers';
import { useChangeTranslations } from 'hooks';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { Suspense, useEffect } from 'react';
import { initReactI18next, useTranslation } from 'react-i18next';
import { addFormatters } from './formaters';

const customNavigatorDetector = {
  name: 'customNavigatorDetector',
  lookup: getNavigatorLanguage,
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(customNavigatorDetector);

const buildHash = env.REACT_APP_BUILD_HASH || 'dev';

const tolgee = Tolgee()
  .use(I18nextPlugin())
  .use(DevTools())
  .use(
    BackendFetch({
      prefix: `/${BASENAME}/i18n`,
      getPath: ({ namespace, language, prefix }) =>
        `${prefix}/${namespace}/${language}.json?v=${buildHash}`,
    }),
  )
  .init({
    apiUrl: env.REACT_APP_TOLGEE_API_URL,
    apiKey: env.REACT_APP_TOLGEE_API_KEY,
    language: DEFAULT_LANGUAGE,
  });

export const i18nInitPromise = withTolgee(i18next, tolgee)
  .use(initReactI18next)
  .use(languageDetector)
  .init({
    fallbackLng: DEFAULT_LANGUAGE,
    ns: ['common', 'navigation', 'forms', 'user', 'address'],
    interpolation: { escapeValue: false },
    react: {
      useSuspense: true,
    },
    detection: {
      order: ['customNavigatorDetector'],
    },
    returnNull: false,
  });

addFormatters(i18next);

type Props = {
  children: JSX.Element;
};

export const I18NextProvider = ({ children }: Props) => {
  const {
    i18n: { language },
  } = useTranslation();
  const [token] = useLocalStorage({
    key: STORAGE_KEYS.token,
    serialize: value => value,
    getInitialValueInEffect: false,
  });

  const { data: userData } = useGetUser({
    enabled: Boolean(token) && token !== 'null',
  });

  const changeTranslations = useChangeTranslations();

  setDefaultOptions({
    locale: userData?.locale === 'pl' ? pl : enUS,
  });

  useEffect(() => {
    if (userData?.locale && userData.locale !== language) {
      changeTranslations(userData.locale);
      setDefaultOptions({
        locale: userData.locale === 'pl' ? pl : enUS,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.locale]);

  // This Suspense is the last line to protect children from rendering before translations are loaded
  return <Suspense>{children}</Suspense>;
};
